/**
 * Utility functions for wallet balance operations
 */

/**
 * Gets the current value of a wallet type from user data
 * @param {Object} data - User data object
 * @param {string} walletType - Type of wallet (rebet_cash, rebet_coin, etc.)
 * @returns {number} Current wallet value
 */
export const getWalletValue = (data, walletType) => {
  const fieldMappings = {
    rebet_cash: data.rebet_cash ?? data.primary_currency_balance ?? 0,
    rebet_coin: data.rebet_coin ?? data.secondary_currency_balance ?? 0,
    not_wagered_rebet_cash: data.not_wagered_rebet_cash ?? data.primary_not_wagered_currency_balance ?? 0,
    casino_cash: data.casino_cash ?? 0,
    p_cash: data.p_cash ?? 0,
    not_wagered_p_cash: data.not_wagered_p_cash ?? 0,
    prediction_cash: data.prediction_cash ?? 0,
    not_wagered_prediction_cash: data.not_wagered_prediction_cash ?? 0,
  };
  return parseFloat(fieldMappings[walletType] ?? data[walletType] ?? 0);
};

/**
 * Maps money_type to the corresponding field name for updates
 * @param {string} money_type - Money type from form
 * @returns {string} Field name for update
 */
export const getWalletTypeByMoneyType = (money_type) => {
  const fieldMappings = {
    "rebet_cash": "primary_currency_balance",
    "rebet_coin": "secondary_currency_balance",
    "casino_cash": "casino_cash",
    "p_cash": "p_cash",
    "prediction_cash": "prediction_cash",
  };
  return fieldMappings[money_type] || money_type;
};

/**
 * Calculates the updated balances based on operation type and amount
 * @param {Object} data - User data object
 * @param {string} operation - Operation type ("credit" or "debit")
 * @param {string} walletType - Type of wallet being updated
 * @param {string|number} amount - Amount to credit/debit
 * @returns {Object} Object containing the fields to update
 * @throws {Error} If amount exceeds available balance
 */
export const calculateUpdatedBalances = (data, operation, walletType, amount) => {
  const numAmount = parseFloat(amount);

  const currentRebetCash = getWalletValue(data, "rebet_cash");
  const currentNotWageredRebetCash = getWalletValue(data, "not_wagered_rebet_cash");

  const currentPCash = getWalletValue(data, "p_cash");
  const currentNotWageredPCash = getWalletValue(data, "not_wagered_p_cash");

  const currentPredictionCash = getWalletValue(data, "prediction_cash");
  const currentNotWageredPredictionCash = getWalletValue(
    data,
    "not_wagered_prediction_cash",
  );

  const updates = {};

  if (operation === "credit") {

    if (walletType === "redeemable_cash") {
      if (numAmount > currentNotWageredRebetCash) {
        throw new Error(`Amount exceeds available Redeemable Cash.`);
      } else {
        updates.primary_not_wagered_currency_balance =
          currentNotWageredRebetCash - numAmount;
      }
    }

    else if (walletType === "redeemable_p_cash") {
      if (numAmount > currentNotWageredPCash) {
        throw new Error(`Amount exceeds available Redeemable Pikkem Cash.`);
      } else {
        updates.not_wagered_p_cash =
          currentNotWageredPCash - numAmount;
      }
    }

    else if (walletType === "redeemable_prediction_cash") {
      if (numAmount > currentNotWageredPredictionCash) {
        throw new Error(
          `Amount exceeds available Redeemable Prediction Cash.`,
        );
      } else {
        updates.not_wagered_prediction_cash =
          currentNotWageredPredictionCash - numAmount;
      }
    }

    else {
      const currentValue = getWalletValue(data, walletType);
      const moneyType = getWalletTypeByMoneyType(walletType);

      updates[moneyType] = currentValue + numAmount;

      if (walletType === "rebet_cash") {
        updates.primary_not_wagered_currency_balance =
          currentNotWageredRebetCash + numAmount;
      }

      if (walletType === "p_cash") {
        updates.not_wagered_p_cash =
          currentNotWageredPCash + numAmount;
      }

      if (walletType === "prediction_cash") {
        updates.not_wagered_prediction_cash =
          currentNotWageredPredictionCash + numAmount;
      }
    }
  }

  else if (operation === "debit") {

    if (walletType === "redeemable_cash") {
      const currentRedeemableCash =
        currentRebetCash - currentNotWageredRebetCash;

      if (numAmount > currentRedeemableCash) {
        throw new Error(`Amount exceeds available Redeemable Cash.`);
      } else {
        updates.primary_not_wagered_currency_balance =
          currentNotWageredRebetCash + numAmount;
      }
    }

    else if (walletType === "redeemable_p_cash") {
      const currentRedeemablePCash =
        currentPCash - currentNotWageredPCash;

      if (numAmount > currentRedeemablePCash) {
        throw new Error(`Amount exceeds available Redeemable Pikkem Cash.`);
      } else {
        updates.not_wagered_p_cash =
          currentNotWageredPCash + numAmount;
      }
    }

    else if (walletType === "redeemable_prediction_cash") {
      const currentRedeemablePredictionCash =
        currentPredictionCash - currentNotWageredPredictionCash;

      if (numAmount > currentRedeemablePredictionCash) {
        throw new Error(
          `Amount exceeds available Redeemable Prediction Cash.`,
        );
      } else {
        updates.not_wagered_prediction_cash =
          currentNotWageredPredictionCash + numAmount;
      }
    }

    else {
      const currentValue = getWalletValue(data, walletType);
      const moneyType = getWalletTypeByMoneyType(walletType);

      updates[moneyType] = currentValue - numAmount;
    }
  }

  return updates;
};
